/**
 * LateCycleBanner — Jul 2026
 *
 * Dismissible warning banner that renders above the Macro Context
 * Strip when the labor-market rule engine has flagged a late-cycle
 * signal (Sahm Rule, JOLTS decline, LFPR drop, wage decel).
 *
 * Data source: GET /api/macro/late-cycle (rule engine in
 * services/late_cycle.py). Payload:
 *   { active, confidence: 'high'|'medium'|'low', triggered_rules,
 *     message, as_of, computed_at }
 *
 * Dismiss behaviour:
 *   • Stored in localStorage under `divitrack.lateCycleDismissed.v1`
 *     as an epoch-ms timestamp.
 *   • Dismissal expires after 24h so the banner re-surfaces the next
 *     day if the signal is still active. Prevents user forgetting
 *     about a still-live warning.
 *   • Cleared automatically when the underlying `as_of` moves — a
 *     new month of data invalidates the dismissal.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'divitrack.lateCycleDismissed.v1';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

const CONFIDENCE_STYLES = {
  high:   'border-rose-400/50   bg-rose-50/70    text-rose-900   dark:border-rose-700/50   dark:bg-rose-950/40   dark:text-rose-100',
  medium: 'border-amber-400/50  bg-amber-50/70   text-amber-900  dark:border-amber-700/50  dark:bg-amber-950/40  dark:text-amber-100',
  low:    'border-yellow-400/50 bg-yellow-50/70  text-yellow-900 dark:border-yellow-700/50 dark:bg-yellow-950/40 dark:text-yellow-100',
};

function readDismissal(asOf) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return false;
    // Invalidate if the underlying month has moved.
    if (asOf && parsed.as_of && parsed.as_of !== asOf) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    if (Date.now() - (parsed.at || 0) > DISMISS_TTL_MS) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
}

export default function LateCycleBanner() {
  const [signal, setSignal] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/macro/late-cycle`);
        if (cancelled) return;
        setSignal(r.data || null);
        setDismissed(readDismissal(r.data?.as_of));
      } catch (e) {
        // Silent — banner just doesn't render.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = () => {
    try {
      localStorage.setItem(
        DISMISS_KEY,
        JSON.stringify({ at: Date.now(), as_of: signal?.as_of || null }),
      );
    } catch (e) { /* localStorage full or blocked — ignore */ }
    setDismissed(true);
  };

  if (!signal || !signal.active || dismissed) return null;

  const confidence = signal.confidence || 'low';
  const styleClass = CONFIDENCE_STYLES[confidence] || CONFIDENCE_STYLES.low;
  const badgeLabel = confidence === 'high' ? 'HIGH CONFIDENCE'
                    : confidence === 'medium' ? 'MEDIUM CONFIDENCE'
                    : 'LOW CONFIDENCE';

  return (
    <div
      className={`mx-2 sm:mx-4 mb-3 rounded-lg border px-4 py-3 flex items-start gap-3 shadow-sm ${styleClass}`}
      data-testid="late-cycle-banner"
    >
      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 text-[13px] leading-snug">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-bold uppercase tracking-wide text-[11px]" data-testid="late-cycle-confidence">
            Late-cycle signal · {badgeLabel}
          </span>
          {signal.as_of && (
            <span className="text-[10px] opacity-70">as of {String(signal.as_of).slice(0, 7)}</span>
          )}
        </div>
        <div data-testid="late-cycle-message">{signal.message}</div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
        title="Dismiss for 24h"
        data-testid="late-cycle-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
